import type { AxiosInstance } from "axios";
import { KeyRound, Loader2, ShieldCheck, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { apiErrorMessage } from "../lib/api.js";
import { createStaff, fetchStaff, setStaffDisabled, type StaffMember } from "../lib/admin.js";

/**
 * Admin-portal staff — the people who can sign into THIS portal.
 *
 * A staff token reaches every organization, so two guards matter and both live in the API:
 * you cannot disable your own account (locking yourself out needs another admin to undo),
 * and disabling one bumps its token version so its live sessions die at once. This screen
 * only exposes those actions and shows the one-time password a new account is created with.
 */
export function StaffScreen({ client, currentAdminId }: { readonly client: AxiosInstance; readonly currentAdminId: string }): React.JSX.Element {
  const [staff, setStaff] = useState<readonly StaffMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStaff(await fetchStaff(client));
      setError(null);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load staff."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex w-full flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Staff</h1>
        <p className="text-muted-foreground text-sm">Who can sign into the admin portal. Every account reaches every organization.</p>
      </header>

      <AddStaff client={client} onAdded={() => void load()} />

      {error !== null && <p className="text-destructive text-sm">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Accounts</CardTitle>
        </CardHeader>
        <CardContent>
          {staff === null ? (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.map((member) => (
                    <StaffRow
                      key={member.id}
                      member={member}
                      isSelf={member.id === currentAdminId}
                      client={client}
                      onChanged={() => void load()}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StaffRow({
  member,
  isSelf,
  client,
  onChanged,
}: {
  readonly member: StaffMember;
  readonly isSelf: boolean;
  readonly client: AxiosInstance;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const disabled = member.disabledAt !== null;

  async function toggle(): Promise<void> {
    setBusy(true);
    try {
      await setStaffDisabled(client, member.id, !disabled);
      onChanged();
    } catch (caught) {
      // The list reload surfaces the true state; a failed toggle simply does not change it.
      void caught;
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <TableRow>
      <TableCell className="font-medium">
        {member.name}
        {isSelf && <span className="text-muted-foreground ml-2 text-xs">(you)</span>}
      </TableCell>
      <TableCell className="text-muted-foreground">{member.email}</TableCell>
      <TableCell>
        {disabled ? <Badge className="border-transparent bg-red-500/15 text-red-700 dark:text-red-400">Disabled</Badge> : <Badge variant="outline">Active</Badge>}
      </TableCell>
      <TableCell className="text-right">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || (isSelf && !disabled)}
          title={isSelf && !disabled ? "You cannot disable your own account" : undefined}
          onClick={() => void toggle()}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {disabled ? "Enable" : "Disable"}
        </Button>
      </TableCell>
    </TableRow>
  );
}

function AddStaff({ client, onAdded }: { readonly client: AxiosInstance; readonly onAdded: () => void }): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setTempPassword(null);
    try {
      const created = await createStaff(client, email.trim(), name.trim());
      setTempPassword({ email: created.email, password: created.temporaryPassword });
      setEmail("");
      setName("");
      onAdded();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not create the staff account."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <form onSubmit={(e) => void submit(e)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="text-primary size-5" /> Add staff
          </CardTitle>
          <CardDescription>They sign in with the one-time password shown below. Staff have no self-serve reset — hand it over securely.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 pt-10">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="staff-email">Email</Label>
              <Input id="staff-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@ecomally.in" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="staff-name">Name</Label>
              <Input id="staff-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Their name" />
            </div>
          </div>

          {error !== null && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}

          {tempPassword !== null && (
            <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
              <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
                <KeyRound className="size-4" /> One-time password for {tempPassword.email}
              </div>
              <code className="bg-card rounded border px-2 py-1 font-mono text-xs break-all select-all">{tempPassword.password}</code>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Shown once. Copy it now and hand it over securely — it is not stored anywhere you can read it again.
              </p>
            </div>
          )}

          <Button type="submit" disabled={busy} className="w-fit">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
            Create account
          </Button>
        </CardContent>
      </form>
    </Card>
  );
}
